import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ImageCard from './ImageCard';
import ImageCarousel from './ImageCarousel';
import ImageGridControls from './ImageGridControls';
import CreateImageGrid from './ImageGrid';
import { motion } from 'framer-motion';
import RetroLoadingDots from './RetroLoadingDots';

const colors = [
  '#00FF9F', // UI Green
  '#FF1493', // Deep pink
  '#0066FF', // Bright blue
  '#FF6B00', // Orange
  '#FF00FF', // Hot pink
  '#FFB800', // Amber
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

const LoadingPlaceholder = () => (
  <motion.div 
    className="relative aspect-square rounded-xl overflow-hidden"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="absolute inset-0 flex items-center justify-center">
      <RetroLoadingDots size="24px" />
    </div>
  </motion.div>
);

const PublicImageGrid = ({ onFavorite }) => {
  // Auth0 state
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();

  // UI states
  const [sortOrder, setSortOrder] = useState('newest');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [isHighlightingLikes, setIsHighlightingLikes] = useState(false);

  // Image and loading states
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  // Favorites state
  const [favorites, setFavorites] = useState(new Set());
  const [localCounts, setLocalCounts] = useState({});

  // Convert favorites to Set if it's an array
  const favoritesSet = useMemo(() => {
    const result = favorites instanceof Set ? favorites : new Set(favorites);
    return result;
  }, [favorites]);

  // Initialize local counts from images
  useEffect(() => {
    const counts = {};
    images.forEach(image => {
      counts[image.id] = image.favoriteCount || 0;
    });
    setLocalCounts(counts);
  }, [images, favoritesSet]);

  // Filter effect cleanup
  useEffect(() => {
    // Removed noisy logging
  }, [showFavoritesOnly, images, favoritesSet]);

  // Carousel state
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [selectedImages, setSelectedImages] = useState(null);

  // Add initialization ref
  const isInitialized = useRef(false);

  // Fetch images with pagination and filtering
  const fetchImages = useCallback(async (pageNum = 1) => {
    try {


      setIsLoading(true);
      setError(null);

      // Prepare headers
      const headers = {
        'Accept': 'application/json'
      };
      
      // Add auth token if user is authenticated
      let token;
      if (isAuthenticated) {
        try {
          token = await getAccessTokenSilently();
          headers.Authorization = `Bearer ${token}`;
        } catch (authError) {
          console.warn('[RETRO] ⚠️ Auth token fetch failed, proceeding without auth:', authError);
        }
      }

      // Build query parameters for images
      const params = new URLSearchParams({
        page: pageNum,
        limit: 20,
        sortBy: sortOrder,
        favorites: showFavoritesOnly ? 'true' : 'false',
        includeNew: 'true'
      });

      // Fetch images
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/images/public?${params}`, {
        headers
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const data = await response.json();
      

      
      if (pageNum === 1) {
        setImages(data.images);
      } else {
        setImages(prev => [...prev, ...data.images]);
      }
      
      setHasMore(data.pagination.hasMore);
      setPage(pageNum);

    } catch (error) {
      console.error('[RETRO] ❌ Error fetching images:', error);
      setError(error.message || 'Failed to load images');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, sortOrder, showFavoritesOnly, favoritesSet]);

  // Fetch user's favorites
  const fetchFavorites = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setFavorites(new Set());
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/favorites?userId=${user.sub}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[RETRO-FAVES] API Error: ${response.status}`);
        }
        return;
      }

      const data = await response.json();

      // Create a Set of favorited image IDs
      let newFavorites = new Set();
      
      if (Array.isArray(data)) {
        // Response is array of favorite objects
        newFavorites = new Set(data.map(fav => fav.imageId));
      } else if (data && data.favorites && Array.isArray(data.favorites)) {
        // Response has favorites array
        newFavorites = new Set(data.favorites.map(fav => fav.imageId));
      } else if (data && data.images && Array.isArray(data.images)) {
        // Response has images array - only include favorited ones
        newFavorites = new Set(
          data.images
            .filter(img => img.isFavorited || img.favorited)
            .map(img => img.id)
        );
      }

      setFavorites(newFavorites);

    } catch (error) {
      console.error('[RETRO-FAVES] Error fetching favorites:', error.message);
      setFavorites(new Set());
    }
  }, [getAccessTokenSilently, isAuthenticated, user]);

  // Combined effect for fetching favorites and images
  useEffect(() => {
    // Only run on mount and when filters change
    const loadData = async () => {
      if (!isInitialized.current) {
        await fetchFavorites();
        isInitialized.current = true;
      }
      await fetchImages(1);
    };
    loadData();
  }, [sortOrder, showFavoritesOnly]); // Remove fetchFavorites, fetchImages from deps

  // Add separate effect for auth changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    } else {
      setFavorites(new Set());
    }
  }, [isAuthenticated]);

  // Handle favoriting/unfavoriting an image
  const handleToggleFavorite = async (imageId) => {
    if (!isAuthenticated) return;

    const wasAlreadyFavorited = favoritesSet.has(imageId);
    const currentCount = localCounts[imageId] || 0;
    const newCount = wasAlreadyFavorited ? Math.max(0, currentCount - 1) : currentCount + 1;

    // Update local count immediately
    setLocalCounts(prev => ({
      ...prev,
      [imageId]: newCount
    }));

    try {
      const token = await getAccessTokenSilently();
      const method = wasAlreadyFavorited ? 'DELETE' : 'POST';
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/favorites/${imageId}`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to update favorite: ${response.status}`);
      }

      // Update favorites Set
      setFavorites(prev => {
        const next = new Set(prev);
        if (wasAlreadyFavorited) {
          next.delete(imageId);
        } else {
          next.add(imageId);
          onFavorite?.();
        }
        return next;
      });

      // Refetch favorites to ensure sync
      await fetchFavorites();

    } catch (error) {
      console.error('[RETRO] ❌ Error updating favorite:', error);
      
      // Revert on error
      setLocalCounts(prev => ({
        ...prev,
        [imageId]: currentCount
      }));
    }
  };

  // Handle loading more images
  const handleLoadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchImages(page + 1);
    }
  }, [isLoading, hasMore, page, fetchImages]);

  // Handle image selection for carousel
  const handleImageSelect = (image) => {
    const index = images.findIndex(img => img.id === image.id);
    if (index !== -1) {
      setSelectedImageIndex(index);
      setSelectedImages(images);
    }
  };

  return (
    <div className="space-y-8">
      <ImageGridControls
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        showFavoritesOnly={showFavoritesOnly}
        setShowFavoritesOnly={setShowFavoritesOnly}
        isHighlightingLikes={isHighlightingLikes}
        setIsHighlightingLikes={setIsHighlightingLikes}
        favoritesCount={favoritesSet.size}
        totalCount={images.length}
        isAuthenticated={isAuthenticated}
      />

      <CreateImageGrid
        images={(showFavoritesOnly 
          ? images.filter(img => favoritesSet.has(img.id))
          : images
        ).map((img, index) => ({
          ...img,
          favoriteCount: localCounts[img.id] || img.favoriteCount || 0,
          animationDelay: index * 0.06 // Reduced from 0.1 to 0.06 for 40% faster cascade
        }))}
        onImageClick={handleImageSelect}
        favorites={favoritesSet}
        onToggleFavorite={handleToggleFavorite}
        isLoading={isLoading}
        hasMore={hasMore && (!showFavoritesOnly || images.some(img => !favoritesSet.has(img.id)))}
        onLoadMore={handleLoadMore}
        isHighlightingLikes={isHighlightingLikes}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        showFavoriteCount={true}
        isAuthenticated={isAuthenticated}
      />

      {selectedImageIndex !== null && selectedImages && (
        <ImageCarousel
          images={selectedImages}
          currentIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
          onNext={() => setSelectedImageIndex((prev) => (prev + 1) % selectedImages.length)}
          onPrev={() => setSelectedImageIndex((prev) => (prev - 1 + selectedImages.length) % selectedImages.length)}
          onToggleFavorite={handleToggleFavorite}
          favorites={favoritesSet}
          userId={user?.sub}
          isAuthenticated={isAuthenticated}
        />
      )}
    </div>
  );
};

export default PublicImageGrid; 